import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import type { EntryBatchTransport, EntryMutation, MutationAcknowledgement } from './types'
import { SessionExpiredError } from './types'
import type { AnchorResetPreview, EffectiveRuleVersion, MatrixDocument, RepeatRule } from '../../packages/domain/src/index'
import type { ImportPreview } from '../owner/importWorkflow'
import { clearActiveWallShareUrl } from '../features/sharing/wall-link-storage'
import type { OwnerRevision } from '../features/operations/contracts'

const EXPECTED_PROJECT_REF = 'tqczsjbroclihyshzmlc'

type PublicEnvironment = {
  url: string
  publishableKey: string
}

function readPublicEnvironment(): PublicEnvironment | null {
  const url = import.meta.env.VITE_SUPABASE_URL?.trim()
  const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim()
  if (!url && !publishableKey) {
    clearActiveWallShareUrl()
    return null
  }
  if (!url || !publishableKey) {
    clearActiveWallShareUrl()
    throw new Error('Both VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY are required.')
  }
  try {
    return validatePublicSupabaseEnvironment(url, publishableKey)
  } catch (error) {
    clearActiveWallShareUrl()
    throw error
  }
}

export function validatePublicSupabaseEnvironment(url: string, publishableKey: string): PublicEnvironment {
  const parsed = new URL(url)
  if (parsed.protocol !== 'https:' || parsed.hostname !== `${EXPECTED_PROJECT_REF}.supabase.co`) {
    throw new Error('Supabase URL does not match the allowlisted Codex staging project.')
  }
  if (!publishableKey.startsWith('sb_publishable_')) {
    throw new Error('Only a Supabase publishable key is permitted in the browser bundle.')
  }
  return { url: parsed.origin, publishableKey }
}

function restrictedFetch(allowedOrigin: string): typeof fetch {
  return async (input, init) => {
    const requestUrl = new URL(input instanceof Request ? input.url : input.toString(), window.location.origin)
    if (requestUrl.origin !== allowedOrigin) throw new Error('Blocked a client request to a non-allowlisted origin.')
    return fetch(input, { ...init, credentials: 'omit', redirect: 'error', referrerPolicy: 'no-referrer' })
  }
}

let singleton: SupabaseClient | null | undefined

export function getSupabaseClient() {
  if (singleton !== undefined) return singleton
  const environment = readPublicEnvironment()
  if (!environment) return singleton = null
  singleton = createClient(environment.url, environment.publishableKey, {
    auth: { autoRefreshToken: true, persistSession: true, detectSessionInUrl: true },
    global: { fetch: restrictedFetch(environment.url) },
  })
  return singleton
}

export function isSupabaseConfigured(): boolean {
  return getSupabaseClient() !== null
}

export function createSupabaseEntryTransport(): EntryBatchTransport {
  return {
    async send(mutations: EntryMutation[]) {
      const client = getSupabaseClient()
      if (!client) throw new Error('Staging sync is not configured in this build.')
      const payload = mutations.map(({ id, operationId, date, outcome, deleted, clientCreatedAt, clientSequence, deviceId }) => ({
        idempotency_key: id,
        operation_id: operationId,
        entry_date: date,
        outcome,
        deleted,
        client_created_at: clientCreatedAt,
        client_sequence: clientSequence,
        device_id: deviceId,
      }))
      const { data, error } = await client.functions.invoke('entry-batch', { body: { mutations: payload } })
      if (error) {
        const status = 'context' in error && error.context instanceof Response ? error.context.status : undefined
        if (status === 401 || /jwt|session|unauthor/i.test(error.message)) throw new SessionExpiredError()
        throw error
      }
      if (!data || !Array.isArray(data.acknowledgements)) throw new Error('Invalid entry-batch response.')
      return data.acknowledgements as MutationAcknowledgement[]
    },
  }
}

export async function signInWithStagingPassword(email: string, password: string) {
  const client = getSupabaseClient()
  if (!client) throw new Error('Staging authentication is not configured in this build.')
  return client.auth.signInWithPassword({ email, password })
}

export async function requestStagingPasswordReset(email: string) {
  const client = getSupabaseClient()
  if (!client) throw new Error('Staging authentication is not configured in this build.')
  return client.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/auth/callback`,
  })
}

export async function updateStagingPassword(password: string) {
  const client = getSupabaseClient()
  if (!client) throw new Error('Staging authentication is not configured in this build.')
  return client.auth.updateUser({ password })
}

export async function signOutOfStaging() {
  clearActiveWallShareUrl()
  const client = getSupabaseClient()
  if (client) await client.auth.signOut({ scope: 'local' })
}

export async function getAuthenticatedOwnerId(): Promise<string | null> {
  const client = getSupabaseClient()
  if (!client) return null
  const { data } = await client.auth.getSession()
  return data.session?.user.id ?? null
}

export type RemoteOwnerDocument = {
  document: MatrixDocument
  accumulationAnchor: string | null
  trustedToday: string
  structureVersion: number
  scoreRevision: number
}

const AUTHORITATIVE_PAGE_SIZE = 1_000

type AuthoritativePage<Row> = {
  data: Row[] | null
  error: unknown
}

export async function collectAuthoritativeOwnerRows<Row>(
  fetchPage: (from: number, to: number) => PromiseLike<AuthoritativePage<Row>>,
): Promise<Row[]> {
  const rows: Row[] = []
  for (let from = 0; ; from += AUTHORITATIVE_PAGE_SIZE) {
    const { data, error } = await fetchPage(from, from + AUTHORITATIVE_PAGE_SIZE - 1)
    if (error) throw error
    const page = data ?? []
    rows.push(...page)
    if (page.length < AUTHORITATIVE_PAGE_SIZE) return rows
  }
}

export async function loadRemoteOwnerDocument(): Promise<RemoteOwnerDocument | null> {
  const client = getSupabaseClient()
  if (!client) return null
  const { data: session } = await client.auth.getSession()
  const ownerId = session.session?.user.id
  if (!ownerId) return null
  const settingsSelect = 'timezone,accumulation_anchor_date,structure_version,score_revision'
  const beforeSettingsResult = await client.from('owner_settings').select(settingsSelect).eq('owner_id', ownerId).maybeSingle()
  if (beforeSettingsResult.error) throw beforeSettingsResult.error
  const [
    profileResult,
    targetResult,
    categoryResult,
    categoryPeriodResult,
    operationResult,
    operationPeriodResult,
    ruleResult,
    entryResult,
  ] = await Promise.all([
    client.from('profiles').select('owner_id').eq('owner_id', ownerId).maybeSingle(),
    client.from('target_versions').select('effective_from,target_score').eq('owner_id', ownerId).order('effective_from', { ascending: false }).limit(1),
    collectAuthoritativeOwnerRows((from, to) => client.from('categories')
      .select('id,source_key,name,display_order').eq('owner_id', ownerId)
      .order('display_order').order('id').range(from, to)),
    collectAuthoritativeOwnerRows((from, to) => client.from('category_active_periods')
      .select('category_id,active_from,active_to_exclusive').eq('owner_id', ownerId)
      .order('category_id').order('active_from').range(from, to)),
    collectAuthoritativeOwnerRows((from, to) => client.from('operations')
      .select('id,category_id,source_key,name,is_private,display_order').eq('owner_id', ownerId)
      .order('display_order').order('id').range(from, to)),
    collectAuthoritativeOwnerRows((from, to) => client.from('operation_active_periods')
      .select('operation_id,active_from,active_to_exclusive').eq('owner_id', ownerId)
      .order('operation_id').order('active_from').range(from, to)),
    collectAuthoritativeOwnerRows((from, to) => client.from('repeat_rule_versions')
      .select('id,operation_id,effective_from,effective_to_exclusive,quota,weekdays,period,part').eq('owner_id', ownerId)
      .order('operation_id').order('effective_from').order('id').range(from, to)),
    collectAuthoritativeOwnerRows((from, to) => client.from('entries')
      .select('operation_id,entry_date,outcome').eq('owner_id', ownerId)
      .order('entry_date').order('operation_id').range(from, to)),
  ])
  const afterSettingsResult = await client.from('owner_settings').select(settingsSelect).eq('owner_id', ownerId).maybeSingle()
  if (afterSettingsResult.error) throw afterSettingsResult.error
  const beforeSettings = beforeSettingsResult.data
  const settings = afterSettingsResult.data
  if (!beforeSettings || !settings
    || beforeSettings.structure_version !== settings.structure_version
    || beforeSettings.score_revision !== settings.score_revision) {
    throw new Error('Owner data changed while loading. Reload again to get one authoritative version.')
  }
  const results = [profileResult, targetResult]
  const failed = results.find((result) => result.error)
  if (failed?.error) throw failed.error
  if (!profileResult.data) return null

  const categorySourceByNative = new Map(categoryResult.map((row) => [row.id, row.source_key]))
  const operationSourceByNative = new Map(operationResult.map((row) => [row.id, row.source_key]))
  const categoryPeriods = new Map<string, Array<{ from: string; toExclusive: string | null }>>()
  for (const row of categoryPeriodResult) {
    const source = categorySourceByNative.get(row.category_id)
    if (!source) continue
    const list = categoryPeriods.get(source) ?? []
    list.push({ from: row.active_from, toExclusive: row.active_to_exclusive })
    categoryPeriods.set(source, list)
  }
  const operationPeriods = new Map<string, Array<{ from: string; toExclusive: string | null }>>()
  for (const row of operationPeriodResult) {
    const source = operationSourceByNative.get(row.operation_id)
    if (!source) continue
    const list = operationPeriods.get(source) ?? []
    list.push({ from: row.active_from, toExclusive: row.active_to_exclusive })
    operationPeriods.set(source, list)
  }
  const rules = new Map<string, EffectiveRuleVersion[]>()
  for (const row of ruleResult) {
    const source = operationSourceByNative.get(row.operation_id)
    if (!source) continue
    const list = rules.get(source) ?? []
    list.push({
      id: row.id,
      effectiveFrom: row.effective_from,
      effectiveToExclusive: row.effective_to_exclusive,
      rule: { quota: row.quota, weekdays: row.weekdays, period: row.period, part: row.part } as RepeatRule,
    })
    rules.set(source, list)
  }
  const operations = operationResult.map((row) => ({
    id: row.source_key,
    sourceKey: row.source_key,
    categoryId: categorySourceByNative.get(row.category_id) ?? '',
    name: row.name,
    private: row.is_private,
    activePeriods: operationPeriods.get(row.source_key) ?? [],
    ruleVersions: rules.get(row.source_key) ?? [],
  }))
  const operationOrderByCategory = new Map<string, string[]>()
  for (const row of operationResult) {
    const category = categorySourceByNative.get(row.category_id)
    if (!category) continue
    operationOrderByCategory.set(category, [...(operationOrderByCategory.get(category) ?? []), row.source_key])
  }
  const document: MatrixDocument = {
    ownerId,
    target: Number(targetResult.data?.[0]?.target_score ?? 80),
    categories: categoryResult.map((row) => ({
      id: row.source_key,
      sourceKey: row.source_key,
      name: row.name,
      activePeriods: categoryPeriods.get(row.source_key) ?? [],
      operationOrder: operationOrderByCategory.get(row.source_key) ?? [],
    })),
    operations,
    entries: entryResult.flatMap((row) => {
      const operationId = operationSourceByNative.get(row.operation_id)
      return operationId ? [{ operationId, date: row.entry_date, outcome: row.outcome as MatrixDocument['entries'][number]['outcome'] }] : []
    }),
  }
  const timezone = settings.timezone ?? 'Australia/Brisbane'
  const trustedToday = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
  return {
    document,
    accumulationAnchor: settings.accumulation_anchor_date ?? null,
    trustedToday,
    structureVersion: settings.structure_version,
    scoreRevision: settings.score_revision,
  }
}

export async function loadRemoteOwnerRevision(): Promise<OwnerRevision> {
  const client = getSupabaseClient()
  if (!client) throw new Error('Staging owner persistence is not configured.')
  const { data, error } = await client.from('owner_settings').select('structure_version,score_revision').single()
  if (error || !data || !Number.isSafeInteger(data.structure_version) || !Number.isSafeInteger(data.score_revision)) {
    throw new Error('Owner revisions could not be refreshed.')
  }
  return { structureVersion: data.structure_version, scoreRevision: data.score_revision }
}

export function authoritativeStructureRevision(
  receiptStructureVersion: number,
  authoritative: RemoteOwnerDocument | null,
): OwnerRevision {
  if (!authoritative || authoritative.structureVersion !== receiptStructureVersion) {
    throw new Error('Owner structure receipt did not match the authoritative owner document.')
  }
  return { structureVersion: authoritative.structureVersion, scoreRevision: authoritative.scoreRevision }
}

export async function saveRemoteOwnerStructure(
  document: MatrixDocument,
  expectedVersion: number,
  effectiveOn: string,
): Promise<OwnerRevision> {
  const client = getSupabaseClient()
  if (!client) throw new Error('Staging owner persistence is not configured.')
  const { data, error } = await client.functions.invoke('owner-structure', {
    body: { document: { ...document, entries: undefined }, expectedVersion, effectiveOn },
  })
  if (error || !data || !Number.isSafeInteger(data.structureVersion)) {
    let reason = ''
    if (error && typeof error === 'object' && 'context' in error && error.context instanceof Response) {
      try {
        const body = await error.context.clone().json() as { error?: unknown }
        if (typeof body.error === 'string') reason = body.error
      } catch {
        // Preserve the stable fallback when the edge response has no JSON body.
      }
    }
    throw new Error(reason || 'Owner structure was not saved.')
  }
  // A revision number alone is not authoritative owner state. Reload the
  // complete revision-bracketed document before returning either revision so
  // callers cannot pair a newer score revision with older entry data.
  const authoritative = await loadRemoteOwnerDocument()
  return authoritativeStructureRevision(data.structureVersion, authoritative)
}

export async function applyRemoteOwnerImport(preview: ImportPreview): Promise<void> {
  const client = getSupabaseClient()
  if (!client) throw new Error('Staging owner import is not configured.')
  const { ownerId, categories, operations, entries } = preview.nextDocument
  const { error } = await client.functions.invoke('owner-import', {
    body: {
      previewFingerprint: preview.fingerprint,
      rollbackFingerprint: preview.rollbackFingerprint,
      rollbackDownloaded: true,
      confirmationText: `IMPORT ${preview.fingerprint.slice(0, 12)}`,
      document: { ownerId, categories, operations, entries },
    },
  })
  if (error) throw new Error('The transactional owner import was rejected.')
}

export async function applyRemoteAnchorReset(preview: AnchorResetPreview, confirmationText: string): Promise<void> {
  const client = getSupabaseClient()
  if (!client) throw new Error('Staging anchor reset is not configured.')
  const { error } = await client.functions.invoke('anchor-reset', {
    body: { preview, confirmationText },
  })
  if (error) throw new Error('The anchor reset was rejected.')
}

// Deliberately no sign-up method: public signup is disabled for this staging app.
